import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import uuid from 'react-uuid';
import classNames from 'classnames';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import CommonDeleteIconButton from '../../common/components/CommonDeleteIconButton';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import CommonDropzone from '../../common/components/CommonDropzone/CommonDropzone';

function AddVideoForm({
  onUpload,
  onDelete,
  videos,
  isLoading,
  validationError,
  className,
  maxVideos = 2,
  videoTypes = ['EXTERIOR VIDEO', 'INTERIOR VIDEO'],
  acceptedFormats = 'video/mp4,video/x-m4v,video/*',
  dropzoneText = 'DRAG & DROP VIDEO HERE',
  errorMessage = 'Upload only image/video format.',
}) {
  const getVideoType = useMemo(
    () => (index) => videoTypes[index] || `VIDEO ${index + 1}`,
    [videoTypes],
  );

  const handleDrop = useCallback(
    (acceptedFiles) => {
      onUpload(acceptedFiles[0]);
    },
    [onUpload],
  );

  const handleDelete = useCallback(
    (mediaMetadataId) => {
      onDelete(mediaMetadataId);
    },
    [onDelete],
  );

  const renderVideo = useCallback(
    (video, index) => {
      const videoTypeLabel = getVideoType(index);
      const videoAltText = `${videoTypeLabel.toLowerCase()} preview`;
      return (
        <Col md={6} className="p-0" key={video.mediaMetadataId || uuid()}>
          <div className="photo-summary__video-wrapper">
            <div
              className="d-flex justify-content-center position-relative overflow-hidden p-1"
              role="img"
              aria-label={videoAltText}
            >
              <div className="videoImageContainer overflow-hidden w-100">
                <img
                  src={video.previewUrl}
                  alt={videoAltText}
                  className="photo-summary__video"
                  loading="lazy"
                />
              </div>
            </div>
            <Row className="mt-2">
              <Col className="d-flex flex-row align-items-center">
                <span
                  className="photo-summary__add-video-title text-sm font-weight-bold"
                  aria-label={`Video type: ${videoTypeLabel}`}
                >
                  {videoTypeLabel}
                </span>
                <CommonDeleteIconButton
                  className="photo-summary__photo-icon-button ml-auto"
                  handleClick={() => handleDelete(video.mediaMetadataId)}
                  aria-label={`Delete ${videoTypeLabel.toLowerCase()}`}
                />
              </Col>
            </Row>
          </div>
        </Col>
      );
    },
    [getVideoType, handleDelete],
  );

  const renderDropzone = useCallback(
    (index) => {
      const videoTypeLabel = getVideoType(index);

      return (
        <Col md={6} className="p-1" key={`dropzone-${index}`}>
          <div className="photo-summary__video-wrapper">
            <CommonDropzone
              disabled={isLoading}
              onDrop={handleDrop}
              accept={acceptedFormats}
              dropzoneText={dropzoneText}
              aria-label={`Upload ${videoTypeLabel.toLowerCase()}`}
            />
            <Row>
              <Col>
                <p
                  className="photo-summary__add-video-title mt-2 text-sm font-weight-bold"
                  aria-label={`Video type: ${videoTypeLabel}`}
                >
                  {videoTypeLabel}
                </p>
              </Col>
            </Row>
          </div>
        </Col>
      );
    },
    [getVideoType, isLoading, handleDrop, acceptedFormats, dropzoneText],
  );

  const videoSlots = useMemo(() => {
    const safeVideos = videos || [];
    const slots = [];
    for (let i = 0; i < maxVideos; i++) {
      slots.push(safeVideos.length > i ? renderVideo(safeVideos[i], i) : renderDropzone(i));
    }
    return slots;
  }, [videos, maxVideos, renderVideo, renderDropzone]);

  const wrapperClassName = classNames('photo-summary__videos-outer-wrapper mt-3', className);

  return (
    <AddVehicleContentWrapper>
      <SettingsSectionHeader
        header="Videos"
        description="You can add 2 videos of the vehicle to enhance your offer."
      />
      <div className={wrapperClassName} role="region" aria-label="Video upload section">
        <Row className="photo-summary__videos-wrapper" role="list">
          {videoSlots}
        </Row>
      </div>
      <Row>
        <Col>
          {validationError && (
            <label className="error-label mt-3" role="alert" aria-live="polite">
              {errorMessage}
            </label>
          )}
        </Col>
      </Row>
    </AddVehicleContentWrapper>
  );
}

AddVideoForm.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      mediaMetadataId: PropTypes.string,
      previewUrl: PropTypes.string.isRequired,
    }),
  ),
  isLoading: PropTypes.bool,
  validationError: PropTypes.bool,
  className: PropTypes.string,
  maxVideos: PropTypes.number,
  videoTypes: PropTypes.arrayOf(PropTypes.string),
  acceptedFormats: PropTypes.string,
  dropzoneText: PropTypes.string,
  errorMessage: PropTypes.string,
};

AddVideoForm.defaultProps = {
  videos: [],
  isLoading: false,
  validationError: false,
  className: '',
  maxVideos: 2,
  videoTypes: ['EXTERIOR VIDEO', 'INTERIOR VIDEO'],
  acceptedFormats: 'video/mp4,video/x-m4v,video/*',
  dropzoneText: 'DRAG & DROP VIDEO HERE',
  errorMessage: 'Upload only image/video format.',
};

export default AddVideoForm;
